import { LettureOdoo } from "../utils/lettureOdoo";
import { apiClient } from "./client";
import { RiferimentiOdoo } from "../types";

export interface Intervento extends RiferimentiOdoo {
  id: string;
  in_ritardo?: boolean;
  odoo_model: "helpdesk.ticket" | "project.task" | null;
  odoo_id: number | null;
  nome: string | null;
  cliente_nome: string | null;
  cliente_id: string | null;
  sede_id: string | null;
  /** Nome della sede collegata, risolto dal backend (sede_id da solo è un UUID). */
  sede_nome?: string | null;
  /** Indirizzo completo della sede, composto dal backend — precompila anche il R.I.T. */
  sede_indirizzo?: string | null;
  /** Nome del Centro Assistenza assegnato, risolto dal backend. */
  centro_nome?: string | null;
  /** Installazione collegata (installation_schedule) — autocomplete "Collegato a installazione". */
  schedule_id: string | null;
  centro_assistenza_id: string | null;
  istituto_vigilanza_id: string | null;
  /// Tag informativo — non un'assegnazione: indica solo che va coordinato
  /// con l'Istituto di Vigilanza, l'intervento resta del Centro Assistenza.
  richiede_vigilanza: boolean;
  /**
   * CON QUALE istituto è congiunto, quando `richiede_vigilanza` è true.
   * Diverso da `istituto_vigilanza_id` qui sopra, che direbbe di CHI è il
   * record — e non è mai valorizzato.
   *
   * Il caso "Altro" (istituto non censito) viaggia nel campo `_nome`: i due
   * non sono mai pieni insieme, ci pensa il backend. Per MOSTRARE il dato non
   * servono entrambi — c'è `congiunto_con` già risolto.
   */
  istituto_vigilanza_congiunto_id: string | null;
  istituto_vigilanza_congiunto_nome: string | null;
  /**
   * Il nome dell'istituto coinvolto, da qualunque delle due colonne venga,
   * risolto dal backend. È quello che si mostra: funziona anche per un
   * operatore tecnico, che non ha il permesso di leggere l'anagrafica degli
   * istituti e quindi non potrebbe risolvere la chiave da sé.
   */
  congiunto_con?: string | null;
  /// Dispositivo (tipicamente la cassaforte) a cui si riferisce l'intervento —
  /// opzionale, permette di consultarne subito la documentazione dal Catalogo.
  dispositivo_catalogo_id: string | null;
  /**
   * Il PEZZO FISICO su cui si è intervenuto, con la sua matricola — non il
   * modello a catalogo qui sopra, che è un'altra cosa. È quello che la scheda
   * Dispositivo conta sotto "Interventi".
   */
  device_id?: string | null;
  /** Matricola del device collegato, risolta dal backend. */
  device_matricola?: string | null;
  data_prevista: string | null;
  /**
   * Orario dell'APPUNTAMENTO — accompagna `data_prevista`, non la data di
   * esecuzione. Colonne `TIME`: arrivano come `"1970-01-01T09:30:00.000Z"` e
   * si leggono affettando la stringa, mai con `new Date().getHours()`. Vedi
   * `utils/orario.ts`.
   */
  orario_inizio: string | null;
  orario_fine: string | null;
  data_completamento: string | null;
  stato: string | null;
  /** Persona incaricata (FK utente); null = non assegnato a nessuno in particolare. */
  assegnato_utente_id: string | null;
  /** Nome dell'assegnatario, risolto dal backend. */
  assegnato_nome?: string | null;
  tecnico: string | null;
  /**
   * Escluso dalla fatturazione perché coperto da garanzia. La colonna esiste
   * dalla Fase 4 ma fino a ora non era collegata a niente: né il backend né il
   * frontend la nominavano.
   */
  in_garanzia: boolean;
  /**
   * Solo sugli interventi annullati: se l'annullamento si fattura. `null` =
   * non ancora deciso. Lo scrivono solo admin e staff; conta solo finché lo
   * stato è annullato, ma resta salvato se lo stato cambia.
   */
  annullamento_fatturabile?: boolean | null;
  /**
   * Calcolato dal backend (`fatturabilitaIntervento` in
   * `backend/src/utils/statiFatturabili.ts`), come `in_ritardo`: la UI lo
   * mostra e non lo ricalcola. `etichetta` è il testo della cella. Le tre cause
   * `annullato*` dicono anche che l'intervento è annullato: è da lì che il
   * pannello sa quando mostrare la decisione.
   */
  fatturabile?: {
    valore: "Sì" | "No" | "Non ancora";
    causa: "garanzia" | "annullato" | "annullato_a_carico_cliente" | "annullato_da_decidere" | null;
    etichetta: string;
  };
  /**
   * In quale sezione del Report Ordini sta il suo OdV, calcolato dal backend
   * con la stessa funzione del report (`sezioneOdv` in `report/datiOrdini.ts`),
   * sull'ORDINE con tutte le attività che copre (issue #211, PR A).
   * `sezione: null` = nessuna sezione: niente OdV, oppure già fatturato.
   * Solo per admin e staff: agli altri ruoli non arriva.
   */
  sezione_odv?: {
    sezione: "da_fatturare" | "non_ancora_fatturabili" | "non_da_fatturare" | "da_qualificare" | null;
    motivo: string | null;
  };
  note: string | null;
  odoo_synced: boolean;
  odoo_last_sync: string | null;
}

export async function createIntervento(payload: Partial<Intervento>): Promise<{ record: Intervento }> {
  const { data } = await apiClient.post<{ record: Intervento }>("/interventi", payload);
  return data;
}

/**
 * Valore di ?stato= che seleziona i record **senza** stato: una stringa vuota
 * nella query string non sarebbe distinguibile da "nessun filtro", e oggi è il
 * caso più frequente (gli interventi da Odoo arrivano con stato nullo).
 * Speculare a STATO_NULLI in backend/src/routes/interventions.ts, dove filtra
 * `stato IS NULL` — lo stesso criterio con cui la Dashboard aggrega la fetta
 * "Senza stato", così i due conteggi combaciano.
 */
export const STATO_NULLI = "__nulli__";

export interface InterventiFilters {
  mese?: number; // 1-12
  /** Ricerca testuale della barra dell'elenco. */
  search?: string;
  anno?: number;
  ritardo?: boolean;
  /** Stato esatto, oppure STATO_NULLI per i record senza stato. */
  stato?: string;
}

export async function listInterventi(filters: InterventiFilters = {}): Promise<{ records: Intervento[] }> {
  const { data } = await apiClient.get<{ records: Intervento[] }>("/interventi", { params: filters });
  return data;
}

/** Campi aggiornabili dal pannello di dettaglio (vedi CAMPI_PATCH lato server). */
export interface InterventoUpdate {
  /**
   * Titolo dell'intervento. Accettato dal backend SOLO sui record locali: su
   * quelli collegati a Odoo viene rifiutato con 400, perché la sync lo
   * riscriverebbe al primo allineamento.
   */
  nome?: string;
  /**
   * Sede dell'anagrafica presso cui si è svolto l'intervento. A differenza del
   * titolo è accettata anche sui record Odoo: la sync non scrive mai questo
   * campo, quindi non c'è nulla che possa sovrascriverla. Impostandola, il
   * backend allinea anche `cliente_id` all'azienda madre della sede.
   */
  sede_id?: string | null;
  stato?: string | null;
  tecnico?: string | null;
  data_prevista?: string | null;
  data_completamento?: string | null;
  /**
   * Orario dell'appuntamento, `"HH:MM"` — riferito a `data_prevista`. Accettato
   * anche sui record Odoo: la sync non scrive queste colonne, che in Odoo non
   * hanno un corrispondente. `null` o stringa vuota tolgono l'orario.
   *
   * Il backend li accetta sulla PATCH solo perché sono stati aggiunti a mano a
   * `CAMPI_PATCH` in `routes/interventions.ts`: quella whitelist è scritta a
   * mano, e un campo nuovo che non ci finisce viene ignorato in silenzio.
   */
  orario_inizio?: string | null;
  orario_fine?: string | null;
  /** Esclude l'intervento dalla fatturazione. */
  in_garanzia?: boolean;
  /**
   * Se un annullamento si fattura: `true` / `false` / `null` (da decidere).
   * Accettato dal backend solo per admin/staff, ignorato per gli altri.
   */
  annullamento_fatturabile?: boolean | null;
  /**
   * Il pezzo fisico su cui si è intervenuto. Una scelta esplicita ha la
   * precedenza sull'eredità dall'installazione collegata; stringa vuota o null
   * scollega.
   */
  device_id?: string | null;
  note?: string | null;
  /**
   * Riassegnazione a un altro Centro Assistenza: accettata dal backend solo
   * per admin/staff, per gli altri viene ignorata in silenzio. Stringa vuota
   * o null = nessun centro assegnato.
   */
  centro_assistenza_id?: string | null;
  /**
   * Persona incaricata. Il backend verifica che appartenga all'organizzazione
   * dell'intervento e rifiuta con 400 altrimenti: assegnare fuori perimetro
   * farebbe uscire il record dallo scope di chi lo riceve. null = nessuno.
   */
  assegnato_utente_id?: string | null;
  /**
   * I cinque riferimenti OdA/OdV. Accettati dal backend solo per admin/staff,
   * per gli altri ignorati in silenzio come `centro_assistenza_id`.
   *
   * Anche loro stanno FUORI da `CAMPI_PATCH` lato server, come `in_garanzia` e
   * `richiede_vigilanza`: quel ciclo normalizza `""` a `null`, che sui due
   * booleani non nullable farebbe fallire la query. Sono gestiti in un blocco
   * loro — vedi `utils/riferimentiOdoo.ts`.
   */
  numero_oda?: string | null;
  oda_fattura_ricevuta?: boolean;
  numero_odv?: string | null;
  odv_in_garanzia?: boolean | null;
  odv_fatturato?: boolean;
}

export async function updateIntervento(id: string, payload: InterventoUpdate): Promise<{ record: Intervento }> {
  const { data } = await apiClient.patch<{ record: Intervento }>(`/interventi/${id}`, payload);
  return data;
}

/**
 * Valori di `stato` già presenti, per suggerirli nel form. Il campo è testo
 * libero (gli stage arrivano da Odoo), quindi non esiste un'enumerazione fissa
 * da cui attingere e resta possibile inserirne uno nuovo.
 */
export async function getStatiInterventi(): Promise<string[]> {
  const { data } = await apiClient.get<{ stati: string[] }>("/interventi/stati");
  return data.stati;
}

export async function syncInterventi(): Promise<{
  synced: number;
  discrepanze: number;
  helpdesk: number;
  field_service: number;
  /**
   * Quanti interventi hanno ricevuto in questo giro il collegamento
   * all'anagrafica, ricavato dall'id del partner Odoo. Contano solo i campi
   * che erano VUOTI: la sync non riscrive un collegamento gia' presente.
   */
  collegati_cliente: number;
  collegati_sede: number;
  /**
   * Sedi trovate ma non collegate perche' appartengono a un'azienda madre
   * diversa da quella gia' scritta sull'intervento. Zero e' il caso normale.
   */
  collegamenti_incoerenti: number;
  /** Quanto erano piene le due letture da Odoo — vedi utils/lettureOdoo.ts. */
  lettura?: LettureOdoo;
}> {
  const { data } = await apiClient.post("/interventi/sync");
  return data;
}

/** Scrive una nota sul record Odoo collegato (Helpdesk o Field Service) — solo se già sincronizzato. */
export async function inviaNotaOdoo(interventoId: string, messaggio: string): Promise<{ success: true }> {
  const { data } = await apiClient.post<{ success: true }>(`/interventi/${interventoId}/nota-odoo`, { messaggio });
  return data;
}

export async function deleteIntervento(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/interventi/${id}`);
  return data;
}

export interface BulkDeleteFilters {
  stato?: string;
  odoo_model?: "helpdesk.ticket" | "project.task";
  data_da?: string;
  data_a?: string;
}

export async function bulkDeletePreview(filters: BulkDeleteFilters): Promise<{ count: number }> {
  const { data } = await apiClient.get<{ count: number }>("/interventi/bulk-delete/preview", { params: filters });
  return data;
}

export async function bulkDeleteInterventi(filters: BulkDeleteFilters): Promise<{ deleted: number }> {
  const { data } = await apiClient.delete<{ deleted: number }>("/interventi/bulk", { params: filters });
  return data;
}
